import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SEOHead from '../common/SEOHead';
import SafeIcon from '../common/SafeIcon';

const { FiHome, FiUsers, FiTool, FiDollarSign, FiShield, FiStar, FiCheck, FiTrendingUp, FiCalendar } = FiIcons;

const ServicesOverviewPage = () => {
  const services = [
    {
      icon: FiHome,
      title: 'Property Management',
      description: 'Full-service property management solutions that handle everything from tenant placement to financial reporting, maximizing your investment returns.',
      link: '/services/property-management',
      features: [
        'Tenant Placement & Screening',
        'Rent Collection & Financial Reporting',
        'Maintenance Coordination',
        'Legal Compliance Management'
      ],
      stats: { increase: '15-25%', satisfaction: '98%' }
    },
    {
      icon: FiUsers,
      title: 'Tenant Placement',
      description: 'Professional tenant acquisition services with comprehensive screening to ensure quality, long-term tenants for your property.',
      link: '/services/tenant-placement',
      features: [
        'Comprehensive Background Checks',
        'Income & Credit Verification',
        'Rental History Assessment',
        'Professional Lease Preparation'
      ],
      stats: { reduction: '90%', retention: '30% longer' }
    },
    {
      icon: FiTool,
      title: 'Maintenance Services',
      description: 'Proactive maintenance programs and 24/7 emergency response to protect your property value and ensure tenant satisfaction.',
      link: '/services/maintenance-services',
      features: [
        '24/7 Emergency Repairs (2hr response)',
        'Preventive Maintenance Scheduling',
        'Quality Contractor Network',
        'Property Inspection Programs'
      ],
      stats: { response: '2 hours', cost: '30% less' }
    },
    {
      icon: FiDollarSign,
      title: 'Financial Reporting',
      description: 'Transparent, CPA-quality financial reporting with real-time access to property performance metrics and tax documentation.',
      link: '/services/financial-reporting',
      features: [
        'Monthly Financial Statements',
        'Tax-Ready Documentation',
        'Performance Analytics',
        'Budget & Forecasting'
      ],
      stats: { access: 'Real-time', accuracy: '100%' }
    },
    {
      icon: FiShield,
      title: 'Legal Compliance',
      description: 'Expert legal compliance services ensuring full adherence to Alberta regulations and protection of your investment interests.',
      link: '/services/legal-compliance',
      features: [
        'Lease Agreement Management',
        'Regulatory Compliance Monitoring',
        'Dispute Resolution',
        'Risk Management Strategies'
      ],
      stats: { compliance: '100%', violations: '0' }
    },
    {
      icon: FiStar,
      title: 'Real Estate Investment',
      description: 'Strategic investment guidance and portfolio management to build long-term wealth through Calgary\'s real estate market.',
      link: '/services/real-estate-investment',
      features: [
        'Property Acquisition Support',
        'Investment Strategy Development',
        'Market Analysis & Research',
        'Portfolio Optimization'
      ],
      stats: { roi: '18% avg', experience: '15+ years' }
    }
  ];

  const schemaData = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Property Management Services Overview",
    "description": "DGM Property Management offers comprehensive property management services including tenant placement, maintenance, financial reporting, legal compliance, and real estate investment strategies.",
    "provider": {
      "@type": "LocalBusiness",
      "name": "DGM Property Management",
      "telephone": "587-800-1215",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Calgary",
        "addressRegion": "Alberta",
        "addressCountry": "CA"
      }
    },
    "areaServed": {
      "@type": "GeoCircle",
      "geoMidpoint": {
        "@type": "GeoCoordinates",
        "latitude": "51.0447",
        "longitude": "-114.0719"
      },
      "geoRadius": "50000"
    },
    "serviceType": ["Property Management", "Tenant Placement", "Maintenance Services", "Financial Reporting", "Legal Compliance", "Real Estate Investment"]
  };

  return (
    <>
      <SEOHead
        title="Property Management Services Overview | DGM Property Management"
        description="DGM Property Management offers comprehensive property management services in Calgary & Airdrie. Tenant placement, maintenance, financial reporting, legal compliance, and investment services. Call 587-800-1215."
        keywords="property management services Calgary, property management overview, tenant placement Calgary, property maintenance Calgary, financial reporting Calgary, legal compliance Calgary, real estate investment Calgary"
        canonical="/services"
        ogImage="https://images.unsplash.com/photo-1560518883-ce09059eeffa?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&h=630"
        schemaData={schemaData}
      />
      
      <div className="min-h-screen bg-gray-50">
        {/* Hero Section */}
        <section className="pt-32 pb-16 bg-gradient-to-br from-blue-600 to-blue-800 text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center"
            >
              <h1 className="text-4xl md:text-6xl font-bold mb-6">
                Property Management Services
              </h1>
              <p className="text-xl text-blue-100 max-w-3xl mx-auto">
                Comprehensive property management solutions designed to maximize your investment returns 
                while providing peace of mind. Professional services across Calgary, Airdrie, and Edmonton.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Services Grid */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Our Comprehensive Services</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                End-to-end property management solutions tailored to Calgary's dynamic real estate market. 
                Click on any service to learn more about our specialized offerings.
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {services.map((service, index) => (
                <motion.div
                  key={service.title}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  whileHover={{ y: -5 }}
                  className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 p-8 border border-gray-100"
                >
                  <div className="flex items-center gap-4 mb-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                      <SafeIcon icon={service.icon} className="w-6 h-6 text-blue-600" />
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900">{service.title}</h3>
                  </div>
                  
                  <p className="text-gray-600 mb-6">{service.description}</p>
                  
                  <div className="mb-6">
                    <h4 className="font-semibold text-gray-900 mb-2">Key Features:</h4>
                    <ul className="space-y-1">
                      {service.features.map((feature, idx) => (
                        <li key={idx} className="text-sm text-gray-700 flex items-center gap-2">
                          <SafeIcon icon={FiCheck} className="w-4 h-4 text-green-500 flex-shrink-0" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-3 mb-6">
                    {service.stats && Object.entries(service.stats).map(([key, value], idx) => (
                      <div key={idx} className="bg-gray-50 rounded-lg p-2 text-center">
                        <div className="text-sm font-bold text-blue-600">{value}</div>
                        <div className="text-xs text-gray-600 capitalize">{key.replace(/([A-Z])/g, ' $1').trim()}</div>
                      </div>
                    ))}
                  </div>
                  
                  <motion.a
                    href={service.link}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg transition-colors duration-200 flex items-center justify-center gap-2"
                  >
                    Learn More
                    <SafeIcon icon={FiTrendingUp} className="w-4 h-4" />
                  </motion.a>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Why Choose Us */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <motion.div
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
              >
                <h2 className="text-4xl font-bold text-gray-900 mb-8">Why Choose Our Services?</h2>
                <div className="space-y-6">
                  <div className="flex gap-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                      <SafeIcon icon={FiHome} className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-gray-900 mb-2">Local Market Expertise</h3>
                      <p className="text-gray-600">
                        15+ years of experience in Calgary, Airdrie, and Edmonton markets with deep understanding 
                        of local regulations, neighborhoods, and rental trends.
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex gap-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                      <SafeIcon icon={FiCalendar} className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-gray-900 mb-2">Proactive Management</h3>
                      <p className="text-gray-600">
                        We don't just react to issues - we anticipate and prevent them. Our proactive approach 
                        maximizes property value and minimizes costly surprises.
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex gap-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                      <SafeIcon icon={FiTrendingUp} className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-gray-900 mb-2">Results-Driven</h3>
                      <p className="text-gray-600">
                        Our clients consistently see 15-25% higher rental income, 50% fewer vacancy days, 
                        and 98% tenant satisfaction rates.
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>
              
              <motion.div
                initial={{ opacity: 0, x: 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
                className="relative"
              >
                <div className="relative overflow-hidden rounded-2xl shadow-xl">
                  <img
                    src="https://images.unsplash.com/photo-1560518883-ce09059eeffa?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&h=600"
                    alt="Professional property management services in Calgary"
                    className="w-full h-[500px] object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-blue-900/50 to-transparent"></div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-blue-600 text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="space-y-6"
            >
              <h2 className="text-4xl font-bold mb-4">Ready to Maximize Your Property Investment?</h2>
              <p className="text-xl text-blue-100 max-w-2xl mx-auto">
                Join hundreds of Calgary property owners who trust DGM Property Management for professional, 
                reliable service that delivers results.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <motion.a
                  href="/contact"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="bg-white text-blue-600 hover:bg-gray-100 px-8 py-4 rounded-lg font-semibold transition-colors duration-200"
                >
                  Get Free Property Analysis
                </motion.a>
                <motion.a
                  href="tel:587-800-1215"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="border-2 border-white hover:bg-white hover:text-blue-600 px-8 py-4 rounded-lg font-semibold transition-colors duration-200"
                >
                  Call 587-800-1215
                </motion.a>
              </div>
            </motion.div>
          </div>
        </section>
      </div>
    </>
  );
};

export default ServicesOverviewPage;